import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridReadyEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { axiomTheme } from '../../styles/designTokens';

type Density = 'compact' | 'standard' | 'comfortable';

type SavedGridView = {
  name: string;
  density: Density;
  quickFilter: string;
  visibleColumnKeys: string[];
};

interface InstitutionalDataGridProps<T = any> {
  rowData: T[];
  columnDefs: ColDef<T>[];
  defaultColDef?: ColDef<T>;
  gridId?: string;
  height?: number | string;
  title?: string;
  subtitle?: string;
  searchPlaceholder?: string;
  initialQuickFilter?: string;
  emptyLabel?: string;
  onRowClicked?: (row: T) => void;
  getRowId?: (row: T) => string;
  enableSearch?: boolean;
  enableExport?: boolean;
  enableDensity?: boolean;
  enableColumnChooser?: boolean;
  enableSavedViews?: boolean;
  pinnedBottomRowData?: T[];
  toolbarExtra?: React.ReactNode;
  pagination?: boolean;
  paginationPageSize?: number;
  rowHeight?: number;
  headerHeight?: number;
  sizeColumnsToFit?: boolean;
  autoHeight?: boolean;
  /**
   * Rows are one server page: sorting and search are delegated to the caller, which
   * re-queries; nothing is sorted or filtered locally. Optional; client grids are unchanged.
   */
  serverSide?: {
    onSortChanged: (sort: { field: string; desc: boolean } | null) => void;
    onSearchChanged: (text: string) => void;
    onExport?: () => void;
  };
}

const Shell = styled.div`
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xxl};
  background: ${axiomTheme.surface.panel};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
  background: ${axiomTheme.surface.panelHeader};
`;

const Title = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-size: 0.9rem;
  font-weight: 850;
`;

const Subtitle = styled.div`
  margin-top: 0.25rem;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.72rem;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SearchInput = styled.input`
  min-width: 220px;
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.lg};
  background: ${axiomTheme.surface.input};
  color: ${axiomTheme.color.text.primary};
  padding: 0.5rem 0.65rem;
  font-size: 0.75rem;
  outline: none;

  &::placeholder {
    color: ${axiomTheme.color.text.faint};
  }

  &:focus {
    border-color: ${axiomTheme.color.border.focus};
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

const GridButton = styled.button<{ $active?: boolean }>`
  border: 1px solid ${p => p.$active ? axiomTheme.color.border.focus : axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.lg};
  background: ${p => p.$active ? 'rgba(56, 189, 248, 0.16)' : axiomTheme.surface.input};
  color: ${p => p.$active ? '#e0f2fe' : axiomTheme.color.text.secondary};
  padding: 0.5rem 0.65rem;
  font-size: 0.68rem;
  font-weight: 850;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: ${axiomTheme.color.text.primary};
    border-color: ${axiomTheme.color.border.strong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

const ToolbarSelect = styled.select`
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.lg};
  background: ${axiomTheme.surface.input};
  color: ${axiomTheme.color.text.secondary};
  padding: 0.5rem 0.65rem;
  min-width: 145px;
  font-size: 0.68rem;
  font-weight: 800;
  outline: none;

  &:focus {
    border-color: ${axiomTheme.color.border.focus};
    box-shadow: ${axiomTheme.shadow.focus};
  }
`;

const ColumnChooserWrap = styled.div`
  position: relative;
`;

const ColumnMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 20;
  width: 260px;
  max-height: 340px;
  overflow-y: auto;
  border: 1px solid ${axiomTheme.color.border.strong};
  border-radius: ${axiomTheme.radius.xl};
  background: rgba(15, 23, 42, 0.98);
  box-shadow: ${axiomTheme.shadow.drawer};
  padding: 0.55rem;
`;

const ColumnOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: ${axiomTheme.radius.md};
  color: ${axiomTheme.color.text.secondary};
  cursor: pointer;
  font-size: 0.74rem;
  padding: 0.42rem 0.45rem;

  &:hover {
    background: rgba(148, 163, 184, 0.08);
    color: ${axiomTheme.color.text.primary};
  }

  input {
    accent-color: ${axiomTheme.color.tone.cyan};
  }
`;

const GridWrap = styled.div<{ $height: number | string; $density: Density; $autoHeight: boolean }>`
  height: ${p => p.$autoHeight ? 'auto' : typeof p.$height === 'number' ? `${p.$height}px` : p.$height};

  .ag-root-wrapper {
    border: 0;
    background: transparent;
  }

  .ag-theme-alpine {
    --ag-background-color: rgba(2, 6, 23, 0.18);
    --ag-foreground-color: ${axiomTheme.color.text.secondary};
    --ag-header-background-color: rgba(15, 23, 42, 0.82);
    --ag-header-foreground-color: ${axiomTheme.color.text.muted};
    --ag-border-color: ${axiomTheme.color.border.subtle};
    --ag-row-border-color: ${axiomTheme.color.border.subtle};
    --ag-odd-row-background-color: rgba(15, 23, 42, 0.18);
    --ag-row-hover-color: rgba(56, 189, 248, 0.08);
    --ag-selected-row-background-color: rgba(56, 189, 248, 0.12);
    --ag-font-family: inherit;
    --ag-font-size: ${p => p.$density === 'compact' ? '11px' : p.$density === 'comfortable' ? '13px' : '12px'};
    --ag-header-height: ${p => p.$density === 'compact' ? '34px' : p.$density === 'comfortable' ? '44px' : '38px'};
    --ag-row-height: ${p => p.$density === 'compact' ? '36px' : p.$density === 'comfortable' ? '52px' : '44px'};
  }

  .ag-header-cell-label {
    font-size: 0.64rem;
    font-weight: 850;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .ag-cell {
    display: flex;
    align-items: center;
  }

  .ag-pinned-bottom-header,
  .ag-pinned-bottom-cols-container .ag-row {
    border-top: 1px solid ${axiomTheme.color.border.strong};
    background: rgba(15, 23, 42, 0.94);
    font-weight: 850;
  }

  .ag-cell-focus,
  .ag-cell-no-focus {
    border-color: transparent !important;
  }
`;

const EmptyOverlay = styled.div`
  color: ${axiomTheme.color.text.muted};
  padding: 2rem;
  text-align: center;
  font-size: 0.82rem;
`;

export function InstitutionalDataGrid<T = any>({
  rowData,
  columnDefs,
  defaultColDef,
  gridId,
  height = 520,
  title,
  subtitle,
  searchPlaceholder = 'Search grid...',
  initialQuickFilter = '',
  emptyLabel = 'No rows',
  onRowClicked,
  getRowId,
  enableSearch = true,
  enableExport = true,
  enableDensity = true,
  enableColumnChooser = true,
  enableSavedViews = true,
  pinnedBottomRowData,
  toolbarExtra,
  pagination = true,
  paginationPageSize = 25,
  rowHeight,
  headerHeight,
  sizeColumnsToFit = true,
  autoHeight = false,
  serverSide,
}: InstitutionalDataGridProps<T>) {
  const [quickFilter, setQuickFilter] = useState(initialQuickFilter);
  const [density, setDensity] = useState<Density>('standard');
  const [gridApi, setGridApi] = useState<any>(null);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);
  const [savedViews, setSavedViews] = useState<SavedGridView[]>([]);
  const [activeViewName, setActiveViewName] = useState('');

  const storageKey = gridId ? `axiomflow:grid:${gridId}:views` : undefined;

  useEffect(() => {
    setQuickFilter(initialQuickFilter);
  }, [initialQuickFilter]);

  const columnMeta = useMemo(() => columnDefs.map((column, index) => {
    const key = String(column.colId || column.field || column.headerName || `col-${index}`);
    return {
      key,
      label: String(column.headerName || column.field || key),
      defaultVisible: column.hide !== true,
    };
  }), [columnDefs]);

  const defaultVisibleColumnKeys = useMemo(
    () => columnMeta.filter(column => column.defaultVisible).map(column => column.key),
    [columnMeta]
  );

  useEffect(() => {
    setVisibleColumnKeys(prev => {
      if (prev.length > 0) {
        const valid = prev.filter(key => columnMeta.some(column => column.key === key));
        const newlyVisibleDefaults = defaultVisibleColumnKeys.filter(key => !valid.includes(key));
        return [...valid, ...newlyVisibleDefaults];
      }
      return defaultVisibleColumnKeys;
    });
  }, [columnMeta, defaultVisibleColumnKeys]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setSavedViews(parsed);
      }
    } catch {
      setSavedViews([]);
    }
  }, [storageKey]);

  const mergedDefaultColDef = useMemo<ColDef<T>>(() => ({
    sortable: true,
    filter: !serverSide,
    resizable: true,
    minWidth: 110,
    flex: 1,
    // A server page arrives already ordered; the header only records the requested sort.
    ...(serverSide ? { comparator: () => 0 } : {}),
    ...defaultColDef,
  }), [defaultColDef, serverSide]);

  const appliedColumnDefs = useMemo<ColDef<T>[]>(() => {
    const activeKeys = visibleColumnKeys.length > 0 ? visibleColumnKeys : defaultVisibleColumnKeys;
    return columnDefs.map((column, index) => {
      const key = String(column.colId || column.field || column.headerName || `col-${index}`);
      return {
        ...column,
        hide: !activeKeys.includes(key),
      };
    });
  }, [columnDefs, defaultVisibleColumnKeys, visibleColumnKeys]);

  const overlayNoRowsTemplate = useMemo(() => `<span>${emptyLabel}</span>`, [emptyLabel]);

  const densityRowHeight = density === 'compact' ? 36 : density === 'comfortable' ? 52 : 44;
  const densityHeaderHeight = density === 'compact' ? 34 : density === 'comfortable' ? 44 : 38;

  const onGridReady = (event: GridReadyEvent<T>) => {
    setGridApi(event.api);
    if (sizeColumnsToFit) {
      event.api.sizeColumnsToFit();
    }
  };

  const persistViews = (next: SavedGridView[]) => {
    setSavedViews(next);
    if (storageKey) {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    }
  };

  const saveCurrentView = () => {
    if (!storageKey) return;
    const name = window.prompt('Save grid view as');
    if (!name?.trim()) return;
    const cleanName = name.trim();
    const nextView: SavedGridView = {
      name: cleanName,
      density,
      quickFilter,
      visibleColumnKeys: visibleColumnKeys.length > 0 ? visibleColumnKeys : defaultVisibleColumnKeys,
    };
    persistViews([...savedViews.filter(view => view.name !== cleanName), nextView]);
    setActiveViewName(cleanName);
  };

  const applySavedView = (name: string) => {
    setActiveViewName(name);
    const view = savedViews.find(item => item.name === name);
    if (!view) return;
    setDensity(view.density);
    setQuickFilter(view.quickFilter);
    setVisibleColumnKeys(view.visibleColumnKeys.filter(key => columnMeta.some(column => column.key === key)));
  };

  const toggleColumn = (key: string) => {
    setVisibleColumnKeys(prev => {
      const activeKeys = prev.length > 0 ? prev : defaultVisibleColumnKeys;
      if (activeKeys.includes(key)) {
        const next = activeKeys.filter(item => item !== key);
        return next.length > 0 ? next : activeKeys;
      }
      return [...activeKeys, key];
    });
    setActiveViewName('');
  };

  return (
    <Shell>
      {(title || subtitle || enableSearch || enableExport || enableDensity || enableColumnChooser || toolbarExtra) && (
        <Header>
          <div>
            {title && <Title>{title}</Title>}
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
          <Toolbar>
            {enableSearch && (
              <SearchInput
                value={quickFilter}
                placeholder={searchPlaceholder}
                onChange={(event) => { setQuickFilter(event.target.value); serverSide?.onSearchChanged(event.target.value); }}
              />
            )}
            {gridId && enableSavedViews && (
              <>
                {savedViews.length > 0 && (
                  <ToolbarSelect value={activeViewName} onChange={(event) => applySavedView(event.target.value)}>
                    <option value="">Default view</option>
                    {savedViews.map(view => <option key={view.name} value={view.name}>{view.name}</option>)}
                  </ToolbarSelect>
                )}
                <GridButton onClick={saveCurrentView}>Save View</GridButton>
              </>
            )}
            {enableDensity && (['compact', 'standard', 'comfortable'] as Density[]).map(option => (
              <GridButton key={option} $active={density === option} onClick={() => setDensity(option)}>
                {option}
              </GridButton>
            ))}
            {enableColumnChooser && columnMeta.length > 0 && (
              <ColumnChooserWrap>
                <GridButton onClick={() => setColumnMenuOpen(open => !open)}>
                  Columns
                </GridButton>
                {columnMenuOpen && (
                  <ColumnMenu>
                    {columnMeta.map(column => (
                      <ColumnOption key={column.key}>
                        <input
                          type="checkbox"
                          checked={(visibleColumnKeys.length > 0 ? visibleColumnKeys : defaultVisibleColumnKeys).includes(column.key)}
                          onChange={() => toggleColumn(column.key)}
                        />
                        {column.label}
                      </ColumnOption>
                    ))}
                  </ColumnMenu>
                )}
              </ColumnChooserWrap>
            )}
            {enableExport && (
              <GridButton onClick={() => (serverSide?.onExport ? serverSide.onExport() : gridApi?.exportDataAsCsv?.())}>
                Export
              </GridButton>
            )}
            {toolbarExtra}
          </Toolbar>
        </Header>
      )}
      <GridWrap $height={height} $density={density} $autoHeight={autoHeight}>
        {rowData.length === 0 ? (
          <EmptyOverlay>{emptyLabel}</EmptyOverlay>
        ) : (
          <div className="ag-theme-alpine" style={{ height: autoHeight ? 'auto' : '100%', width: '100%' }}>
            <AgGridReact<T>
              rowData={rowData}
              columnDefs={appliedColumnDefs}
              defaultColDef={mergedDefaultColDef}
              animateRows
              suppressCellFocus
              quickFilterText={serverSide ? undefined : quickFilter}
              onSortChanged={serverSide ? event => {
                const state = ((event as any).columnApi?.getColumnState?.() ?? (event.api as any).getColumnState?.() ?? []) as { colId: string; sort?: string | null }[];
                const sorted = state.find(column => column.sort);
                serverSide.onSortChanged(sorted ? { field: sorted.colId, desc: sorted.sort === 'desc' } : null);
              } : undefined}
              overlayNoRowsTemplate={overlayNoRowsTemplate}
              rowSelection="single"
              domLayout={autoHeight ? 'autoHeight' : 'normal'}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              pinnedBottomRowData={pinnedBottomRowData}
              rowHeight={rowHeight || densityRowHeight}
              headerHeight={headerHeight || densityHeaderHeight}
              onGridReady={onGridReady}
              onRowClicked={event => {
                // AG Grid listens natively on the row, so a React child's
                // stopPropagation cannot cancel it — clicking an in-row action
                // button fired the row handler too, stacking a drawer behind the
                // action's own modal. Cells that opt out mark themselves with
                // data-grid-no-row-click; every other grid is unaffected.
                const target = (event.event?.target as HTMLElement | null) ?? null;
                if (target?.closest?.('[data-grid-no-row-click]')) return;
                if (event.data) onRowClicked?.(event.data);
              }}
              getRowId={getRowId ? params => getRowId(params.data) : undefined}
            />
          </div>
        )}
      </GridWrap>
    </Shell>
  );
}

export default InstitutionalDataGrid;
